import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { MipsApiService } from '@services/mips-api.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { openBlobInNewTab, downloadBlob } from '../../../utils/blob-viewer.util';
import { MipsFileRepositoryActions } from '@store/actions/mips-file-repository/mips-file-repository.actions';

@Injectable()
export class MipsFileRepositoryEffects {

  getFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsFileRepositoryActions.getFiles),
      switchMap(() =>
        this.mipsApiService.getFileRepository().pipe(
          map((files) =>
            MipsFileRepositoryActions.getFilesSuccess({ files })
          ),
          catchError((error) =>
            of(
              MipsFileRepositoryActions.getFilesFailure({
                error: error.message || 'Failed to load file repository',
              })
            )
          )
        )
      )
    )
  );

  getFilesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsFileRepositoryActions.getFilesFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            'Failed to load the file repository. Please try again.',
            'File Repository Error'
          );
        })
      ),
    { dispatch: false }
  );

  downloadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsFileRepositoryActions.downloadDocument),
      switchMap(({ submissionId, documentId, fileName }) =>
        this.mipsApiService.downloadRepositoryDocument(submissionId, documentId).pipe(
          map((blob) =>
            MipsFileRepositoryActions.downloadDocumentSuccess({ blob, fileName })
          ),
          catchError((error) =>
            of(
              MipsFileRepositoryActions.downloadDocumentFailure({
                error: error?.message || 'Failed to download document',
              })
            )
          )
        )
      )
    )
  );

  downloadDocumentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsFileRepositoryActions.downloadDocumentSuccess),
        tap(({ blob, fileName }) => downloadBlob(blob, fileName))
      ),
    { dispatch: false }
  );

  downloadDocumentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsFileRepositoryActions.downloadDocumentFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            'Failed to download the document. Please try again.',
            'Download Error'
          );
        })
      ),
    { dispatch: false }
  );

  viewDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsFileRepositoryActions.viewDocument),
      switchMap(({ submissionId, documentId, fileName }) =>
        this.mipsApiService.viewRepositoryDocument(submissionId, documentId).pipe(
          map((blob) =>
            MipsFileRepositoryActions.viewDocumentSuccess({ blob, fileName })
          ),
          catchError((error) =>
            of(
              MipsFileRepositoryActions.viewDocumentFailure({
                error: error?.message || 'Failed to view document',
              })
            )
          )
        )
      )
    )
  );

  viewDocumentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsFileRepositoryActions.viewDocumentSuccess),
        tap(({ blob }) => {
          openBlobInNewTab(blob);
        })
      ),
    { dispatch: false }
  );

  viewDocumentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsFileRepositoryActions.viewDocumentFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            'Failed to view the document. Please try again.',
            'View Error'
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private mipsApiService: MipsApiService,
    private errorDialogService: ErrorDialogService,
  ) {}
}
